"use client";

import { Box, Center, type BoxProps } from "@chakra-ui/react";
import type { MindstringPrototype } from "@/generated/fastapi";
import {
  mindstringPrototypesSlang,
  type MindstringPrototypeUiMetadataForFrontend,
} from "@/shared/config/backend/mindstrings";
import { getPrototypeTone } from "@/shared/config/prototypeTheme";
import GenericIcon from "../actions/GenericIcon";

type ProfilePerspectivesMotifSize = "sm" | "md" | "lg";
type ProfilePerspectivesMotifVariant = "cluster" | "splash";

type ProfilePerspectivesMotifProps = {
  opacity?: string;
  showLines?: boolean;
  size?: ProfilePerspectivesMotifSize;
  variant?: ProfilePerspectivesMotifVariant;
};

type MotifItem = {
  prototype: MindstringPrototype;
  h: string;
  left: string;
  rounded: string;
  top: string;
  transform: string;
  w: string;
};

const MOTIF_SIZE = {
  sm: {
    h: "6.75rem",
    icon: "5",
    w: "11.5rem",
  },
  md: {
    h: "8rem",
    icon: "6",
    w: "14rem",
  },
  lg: {
    h: "9.25rem",
    icon: "7",
    w: "16rem",
  },
} as const;

const MOTIF_ITEMS = [
  {
    prototype: "job_activity",
    top: "5%",
    left: "5%",
    w: "45%",
    h: "28%",
    rounded: "full",
    transform: "rotate(5deg)",
  },
  {
    prototype: "learning_outcome",
    top: "10%",
    left: "54%",
    w: "42%",
    h: "30%",
    rounded: "lg",
    transform: "rotate(-3deg)",
  },
  {
    prototype: "workplace_characteristic",
    top: "61%",
    left: "6%",
    w: "44%",
    h: "30%",
    rounded: "lg",
    transform: "rotate(-10deg)",
  },
  {
    prototype: "individual_description",
    top: "58%",
    left: "54%",
    w: "43%",
    h: "31%",
    rounded: "full",
    transform: "rotate(6deg)",
  },
] as const satisfies readonly MotifItem[];

const MOTIF_LINES = [
  {
    prototype: "job_activity",
    path: "M 27 24 L 48 50",
  },
  {
    prototype: "learning_outcome",
    path: "M 73 26 L 52 50",
  },
  {
    prototype: "workplace_characteristic",
    path: "M 28 75 L 48 52",
  },
  {
    prototype: "individual_description",
    path: "M 72 73 L 52 52",
  },
] as const satisfies readonly {
  prototype: MindstringPrototype;
  path: string;
}[];

type SplashShapeProps = {
  h: BoxProps["h"];
  left: BoxProps["left"];
  prototype: MindstringPrototype;
  rounded: BoxProps["rounded"];
  top: BoxProps["top"];
  transform: BoxProps["transform"];
  w: BoxProps["w"];
};

function SplashShape({
  h,
  left,
  prototype,
  rounded,
  top,
  transform,
  w,
}: SplashShapeProps) {
  const tone = getPrototypeTone(prototype);

  return (
    <Box
      position="absolute"
      top={top}
      left={left}
      w={w}
      h={h}
      rounded={rounded}
      bg={tone.accentBg}
      transform={transform}
    />
  );
}

function ProfilePerspectivesSplash({ opacity }: { opacity: string }) {
  return (
    <Box
      position="relative"
      w={{ base: "24rem", md: "44rem" }}
      h={{ base: "31rem", md: "35rem" }}
      opacity={opacity}
      aria-hidden="true"
      pointerEvents="none"
    >
      <SplashShape
        prototype="job_activity"
        top={{ base: "-1.15rem", md: "3.75rem" }}
        left={{ base: "-2.75rem", md: "-1.75rem" }}
        w={{ base: "9.75rem", md: "17.5rem" }}
        h={{ base: "3.35rem", md: "5.25rem" }}
        rounded="full"
        transform="rotate(11deg)"
      />
      <SplashShape
        prototype="learning_outcome"
        top={{ base: "-0.85rem", md: "5rem" }}
        left={{ base: "17.25rem", md: "29.25rem" }}
        w={{ base: "9.5rem", md: "17.25rem" }}
        h={{ base: "3.4rem", md: "5.35rem" }}
        rounded="xl"
        transform="rotate(-8deg)"
      />
      <SplashShape
        prototype="workplace_characteristic"
        top={{ base: "29rem", md: "27.25rem" }}
        left={{ base: "-2.45rem", md: "-1.25rem" }}
        w={{ base: "10rem", md: "18rem" }}
        h={{ base: "3.55rem", md: "5.5rem" }}
        rounded="xl"
        transform="rotate(-14deg)"
      />
      <SplashShape
        prototype="individual_description"
        top={{ base: "28.65rem", md: "26rem" }}
        left={{ base: "17.1rem", md: "28.5rem" }}
        w={{ base: "10.25rem", md: "18.5rem" }}
        h={{ base: "3.7rem", md: "5.75rem" }}
        rounded="full"
        transform="rotate(12deg)"
      />
    </Box>
  );
}

export default function ProfilePerspectivesMotif({
  opacity = "1",
  showLines = true,
  size = "md",
  variant = "cluster",
}: ProfilePerspectivesMotifProps) {
  if (variant === "splash") {
    return <ProfilePerspectivesSplash opacity={opacity} />;
  }

  const dimensions = MOTIF_SIZE[size];

  return (
    <Box
      position="relative"
      w={dimensions.w}
      h={dimensions.h}
      opacity={opacity}
      aria-hidden="true"
      pointerEvents="none"
    >
      {showLines
        ? MOTIF_LINES.map((line) => {
            const tone = getPrototypeTone(line.prototype);

            return (
              <Box
                key={line.prototype}
                position="absolute"
                inset="0"
                color={tone.accent}
                zIndex="0"
              >
                <svg
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                  width="100%"
                  height="100%"
                >
                  <path
                    d={line.path}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="0.65"
                    strokeLinecap="round"
                    strokeDasharray="2 1.5"
                    opacity="0.42"
                  />
                </svg>
              </Box>
            );
          })
        : null}

      <Center
        position="absolute"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        boxSize={dimensions.icon}
        rounded="full"
        bg="moveon.primary"
        boxShadow="sm"
        zIndex="2"
      />

      {MOTIF_ITEMS.map((item) => {
        const metadata: MindstringPrototypeUiMetadataForFrontend =
          mindstringPrototypesSlang[item.prototype];
        const tone = getPrototypeTone(item.prototype);

        return (
          <Box
            key={item.prototype}
            position="absolute"
            top={item.top}
            left={item.left}
            w={item.w}
            h={item.h}
            rounded={item.rounded}
            bg={tone.accentBg}
            borderWidth="1"
            borderColor={tone.accentBorder}
            transform={item.transform}
            zIndex="1"
          >
            <Center
              position="absolute"
              top="50%"
              left="50%"
              transform="translate(-50%, -50%)"
              boxSize={dimensions.icon}
              rounded="full"
              bg="moveon.surface.bg"
              color={tone.accent}
              boxShadow="sm"
            >
              <GenericIcon icon={metadata.icon} size="xs" color={tone.accent} />
            </Center>
          </Box>
        );
      })}
    </Box>
  );
}
